import {
  gaqlchainIs as chainIs
} from '../gaqlchain.js'

import {
  gaqlenumSORTBY as SORTBY,
  gaqlenumSORTDIRECTION as SORTDIRECTION,
  gaqlenumSORTORDERDESC as SORTORDERDESC,
  gaqlenumSORTORDERASC as SORTORDERASC
} from '../gaqlenum.js'

import {
  gaqlasyncarrReduce as asyncarrReduce,
  gaqlasyncarrMap as asyncarrMap,
  gaqlasyncarrFilter as asyncarrFilter
} from '../gaqlasyncarr.js'

import {
  gaqlutilObjCompareProp as utilObjCompareProp,
  gaqlutilQueryArgsOptGet as utilQueryArgsOptGet,
  gaqlutilPlainObjIs as utilPlainObjIs
} from '../gaqlutil.js'

import {
  gaqlerrArgsNumber as errArgsNumber,
  gaqlerrIndexOutOfBounds as errIndexOutOfBounds,
  gaqlerrCannotCallFOOonBARTYPEvalue as errCannotCallFOOonBARTYPEvalue
} from '../gaqlerr.js'

// created by 'asc' and 'desc' queries
const sortObjIs = obj => utilPlainObjIs(obj) && SORTBY in obj
const sortObjParse = o => sortObjIs(o) ? o : null

// Transform each element of one or more sequences by applying a mapping
// function to them. If map is run with two or more sequences, it will iterate
// for as many items as there are in the shortest sequence.
// ```
// sequence1.map([sequence2, ...], function) → stream
// array1.map([array2, ...], function) → array
// q.map(sequence1[, sequence2, ...], function) → stream
// q.map(array1[, array2, ...], function) → array
// ```
const $map = async (gst, qst, args, run) => {
  const arr = qst.target

  if (!Array.isArray(arr))
    throw errCannotCallFOOonBARTYPEvalue('map()', typeof arr)

  qst.target = await asyncarrMap(arr.slice(), async listoldi => (
    run(gst, qst, args[0], [listoldi])))

  return qst
}

// Concatenate one or more elements into a single sequence using a mapping
// function.
//
// concatMap works in a similar fashion to map, applying the given function to
// each element in a sequence, but it will always return a single sequence. If
// the mapping function returns a sequence, map would produce a sequence of
// sequences
// ```
// stream.concatMap(function) → stream
// array.concatMap(function) → array
// ```
const $concatMap = async (gst, qst, args, run) => {
  const func = args[0]

  qst.target = await asyncarrMap(
    qst.target, async t => run(gst, qst, func, [t]))
  qst.target = qst.target.flat()

  return qst
}

// Sort the sequence by document values of the given key(s). To specify the
// ordering, wrap the attribute with either q.asc or q.desc (defaults to
// ascending).
//
// Note: RethinkDB uses byte-wise ordering for orderBy and does not support
// Unicode collations; non-ASCII characters will be sorted by UTF-8 codepoint.
// For more information on RethinkDB’s sorting order, read the section in ReQL
// data types.
//
// Sorting without an index requires the server to hold the sequence in memory,
// and is limited to 100,000 documents (or the setting of the arrayLimit option
// for run). Sorting with an index can be done on arbitrarily large tables, or
// after a between command using the same index. This applies to both secondary
// indexes and the primary key (e.g., {index: 'id'}).
//
// Sorting functions passed to orderBy must be deterministic. You cannot, for
// instance, order rows using the random command. Using a non-deterministic
// function with orderBy will raise a ReqlQueryLogicError.
// ```
// table.orderBy([key | function...], {index: index_name}) → table_slice
// selection.orderBy(key | function[, ...]) → selection<array>
// sequence.orderBy(key | function[, ...]) → array
// ```
const $orderBy = async (gst, qst, args, run) => {
  const queryTarget = qst.target
  const indexSortDirection = SORTORDERASC
  const argsSortPropValue = typeof args[0] === 'string' && args[0]
  let fieldSortDirection = (
    typeof queryTarget === 'object' && queryTarget[SORTDIRECTION])
  const sortDirectionDefault = () => (
    fieldSortDirection || indexSortDirection)
  const sortDirection = (isAscending, dir = sortDirectionDefault()) => (
    isAscending * (dir === SORTORDERASC ? 1 : -1))

  if (!args.length)
    throw errArgsNumber('orderBy', 1, args.length)

  const getSortFieldValue = async doc => {
    let value

    const key = argsSortPropValue ||
      await run(gst, qst, args[0], [doc])

    const sortObj = sortObjParse(key)
    if (sortObj) {
      if (sortObj[SORTDIRECTION]) {
        fieldSortDirection = sortObj[SORTDIRECTION]
      }

      value = sortObj[SORTBY]
    } else {
      // assumes query-derived value represents final value not prop
      value = argsSortPropValue
        ? doc[key]
        : key
    }

    return value
  }

  const queryTargetResolved = await asyncarrMap(queryTarget,
    async (doc, i) => [await getSortFieldValue(doc, i), doc])

  qst.target = queryTargetResolved.sort((doctupa, doctupb) => (
    sortDirection(doctupa[0] < doctupb[0] ? -1 : 1)
  )).map(doctup => doctup[1])

  return qst
}

const $desc = async (gst, qst, args, run) => {
  qst.target = {
    [SORTBY]: await run(gst, qst, args[0], [qst.target]),
    [SORTDIRECTION]: SORTORDERDESC
  }

  return qst
}

const $asc = async (gst, qst, args, run) => {
  qst.target = {
    [SORTBY]: await run(gst, qst, args[0], [qst.target]),
    [SORTDIRECTION]: SORTORDERASC
  }

  return qst
}

// Skip a number of elements from the head of the sequence.
// ```
// sequence.skip(n) → stream
// array.skip(n) → array
// ```
const $skip = async (gst, qst, args, run) => {
  const count = await run(gst, qst, args[0])

  qst.target = qst.target.slice(count)

  return qst
}

// End the sequence after the given number of elements.
// ```
// sequence.limit(n) → stream
// array.limit(n) → array
// ```
const $limit = async (gst, qst, args, run) => {
  const limit = await run(gst, qst, args[0])

  qst.target = qst.target.slice(0, limit)

  return qst
}

// Return the elements of a sequence within the specified range.
//
// slice returns the range between startOffset and endOffset. If only
// startOffset is specified, slice returns the range from that index to the end
// of the sequence. Specify leftBound or rightBound as open or closed to
// indicate whether to include that endpoint of the range by default: closed
// returns that endpoint, while open does not. By default, leftBound is closed
// and rightBound is open, so the range (10,13) will return the tenth, eleventh
// and twelfth elements in the sequence.
//
// If endOffset is past the end of the sequence, all elements from startOffset
// to the end of the sequence will be returned. If startOffset is past the end
// of the sequence or endOffset is less than startOffset, a zero-element
// sequence will be returned.
//
// Negative startOffset and endOffset values are allowed with arrays; in that
// case, the returned range counts back from the array’s end. That is, the
// range (-2) returns the last two elements, and the range of (2,-1) returns the
// second element through the next-to-last element of the range. An error will
// be raised on a negative startOffset or endOffset with non-arrays. (An
// endOffset of −1 is allowed with a stream if rightBound is closed; this
// behaves as if no endOffset was specified.)
//
// If slice is used with a binary object, the indexes refer to byte positions
// within the object. That is, the range (10,20) will refer to the 10th byte
// through the 19th byte.
//
// With a string, slice behaves similarly, with the indexes referring to Unicode
// codepoints. String indexes start at 0. (Note that combining codepoints are
// counted separately.)
// ```
// selection.slice(startOffset[, endOffset, {
//   leftBound:'closed', rightBound:'open'}]) → selection
// stream.slice(startOffset[, endOffset, {
//   leftBound:'closed', rightBound:'open'}]) → stream
// array.slice(startOffset[, endOffset, {
//   leftBound:'closed', rightBound:'open'}]) → array
// binary.slice(startOffset[, endOffset, {
//   leftBound:'closed', rightBound:'open'}]) → binary
// string.slice(startOffset[, endOffset, {
//   leftBound:'closed', rightBound:'open'}]) → string
// ```
const $slice = async (gst, qst, args, run) => {
  const begin = await run(gst, qst, args[0])
  const end = await run(gst, qst, args[1])

  if (qst.isGrouped) { // slice from each group
    qst.target = qst.target.map(targetGroup => {
      targetGroup.reduction = targetGroup.reduction.slice(begin, end)

      return targetGroup
    })
  } else {
    qst.target = qst.target.slice(begin, end)
  }

  return qst
}

// Return all the elements in a sequence for which the given predicate is true.
// The return value of filter will be the same as the input (sequence, stream,
// or array). Documents can be filtered in a variety of ways—ranges, nested
// values, boolean conditions, and the results of anonymous functions.
//
// By default, filter will silently skip documents with missing fields: if the
// predicate tries to access a field that doesn’t exist (for instance, the
// predicate {age: 30} applied to a document with no age field), that document
// will not be returned in the result set, and no error will be generated. This
// behavior can be changed with the default optional argument.
// ```
// selection.filter(predicate_function[, {default: false}]) → selection
// stream.filter(predicate_function[, {default: false}]) → stream
// array.filter(predicate_function[, {default: false}]) → array
// ```
const $filter = async (gst, qst, args, run) => {
  qst.target = await asyncarrFilter(qst.target, async item => {
    const finitem = await run(gst, qst, args[0], [item])

    if (finitem && typeof finitem === 'object') {
      return Object
        .keys(finitem)
        .every(key => finitem[key] === item[key])
    }

    return finitem
  })

  return qst
}

// Get the indexes of an element in a sequence. If the argument is a predicate,
// get the indexes of all elements matching it.
//
// ```
// sequence.offsetsOf(datum | predicate_function) → array
// ```
const $offsetsOf = async (gst, qst, args, run) => {
  const target = qst.target
  const datumOrFn = args[0]
  const datumOrFnChainIs = chainIs(datumOrFn)

  if (!args.length)
    throw errArgsNumber('offsetsOf', 1, args.length)

  qst.target = await asyncarrReduce(target, async (ac, arg, i) => {
    if (datumOrFnChainIs) {
      if (await run(gst, qst, datumOrFn, [arg])) {
        ac.push(i)
      }
    } else if (arg === datumOrFn) {
      ac.push(i)
    }

    return ac
  }, [])

  return qst
}

// Get the nth element of a sequence, counting from zero. If the argument is
// negative, count from the last element.
// ```
// sequence.nth(index) → object
// selection.nth(index) → selection<object>
// ```
const $nth = async (gst, qst, args, run) => {
  const nthIndex = await run(gst, qst, args[0])
  const target = qst.target

  if (!args.length)
    throw errArgsNumber('nth', 1, args.length)
  if (nthIndex >= target.length) {
    throw errIndexOutOfBounds(nthIndex)
  }

  qst.target = nthIndex >= 0
    ? target[nthIndex]
    : target.slice(nthIndex)[0]

  return qst
}

// Test if a sequence is empty.
// ```
// sequence.isEmpty() → bool
// ```
const $isEmpty = async (gst, qst) => {
  qst.target = qst.target.length === 0

  return qst
}

const $union = async (gst, qst, args, run) => {
  const queryOptions = utilQueryArgsOptGet(args, null)

  if (queryOptions)
    args.splice(-1, 1)

  // let res = args.reduce((acc, arg) => {
  let res = await asyncarrReduce(args, async (ac, arg) => {
    return ac.concat(await run(gst, qst, arg))
  }, qst.target || [])

  if (queryOptions && queryOptions.interleave) {
    res = res.sort(
      (a, b) => utilObjCompareProp(a, b, queryOptions.interleave)
    )
  }

  qst.target = res

  return qst
}

// Select a given number of elements from a sequence with uniform random
// distribution. Selection is done without replacement.
//
// If the sequence has less than the requested number of elements (i.e., calling
// sample(10) on a sequence with only five elements), sample will return the
// entire sequence in a random order.
// ```
// sequence.sample(number) → selection
// stream.sample(number) → array
// array.sample(number) → array
// ```
const $sample = (gst, qst, args) => {
  qst.target = qst.target
    .sort(() => 0.5 - Math.random())
    .slice(0, args[0])

  return qst
}

export {
  $map,
  $concatMap,
  $orderBy,
  $desc,
  $asc,
  $slice,
  $skip,
  $limit,
  $filter,
  $nth,
  $offsetsOf,
  $isEmpty,
  $union,
  $sample
}
